import React from 'react';
import { Table, Tag, Tooltip, Space, Button } from 'antd';
import { WarningFilled, EyeOutlined } from '@ant-design/icons';
import { formatDate, STATUT_MAP } from '../../../variables/constants';
import TagRc from '../../../components/TagRc';
import { PastilleNonAffecte } from './NonAffecte';
import BadgeCanal from './mobile/BadgeCanal';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../../i18n/libelles';

const GREEN = '#1a6b3c';

const MUTED = '#6b7280';

const TYPE_META = avecLibelleTraduit({
  SURVENU_TOGO: { label: 'ET', color: 'geekblue' },
  SURVENU_ETRANGER: { label: 'TE', color: 'purple' },
}, 'dossierSinistre:tableMembresLiaison.typesSinistre', { champ: 'text' });
const DOMMAGE_META = avecLibelleTraduit({
  CORPOREL: { color: 'red' },
  MATERIEL: { color: 'orange' },
  MIXTE: { color: 'volcano' },
}, 'dossierSinistre:tableMembresLiaison.typesDommage');

const Dash = () => <span style={{ color: '#9ca3af' }}>-</span>;

const MetaLine = ({ children, strong }) => (
  <div style={{
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: strong ? '#111827' : MUTED, fontSize: strong ? 13 : 12, fontWeight: strong ? 650 : 400,
  }}>
    {children || <Dash />}
  </div>
);

// Copie de renderRc(r) de ListSinistres.js : même règle d'affichage du RC ici.
const renderRc = (m) => {
  if (!m.positionRc || m.positionRc === 'EN_ATTENTE') {
    const enPhaseRc = ['ATTENTE_RC', 'ATTENTE_PIECES_DE_RECLAMATION', 'MUR'].includes(m.statut);
    if (!enPhaseRc) return <Dash />;
  }
  return <TagRc positionRc={m.positionRc} pourcentageRcFinal={m.tauxRc != null ? Number(m.tauxRc) : null} size="sm" />;
};

/**
 * Membres d'une liaison, déroulés sous la ligne - ou, plus généralement, tout
 * sous-tableau de sinistres qu'on veut afficher sans reconstruire ses colonnes
 * à chaque fois.
 *
 * Partagé par la page dédiée aux liaisons et par la liste des sinistres, pour
 * qu'un frère se lise partout de la même façon. Fonctionne aussi bien avec des
 * membres de liaison qu'avec des sinistres ordinaires : les champs propres aux
 * liaisons (moinsAvance, redacteurNom) sont optionnels et retombent sur '-' quand
 * l'appelant passe des sinistres non liés.
 *
 * Les données sont TOUJOURS fournies par l'appelant, qui les a chargées en un
 * seul appel pour toute sa page. Ce sous-tableau ne fait donc AUCUN appel
 * réseau : y ajouter un `useEffect` réintroduirait le N+1 que tout ce module
 * écarte.
 */
export default function TableMembresLiaison({ membres, onOuvrir }) {
  const { t } = useTranslation('dossierSinistre');

  /* Le membre porte le dossier entier sous `sinistre`, rendu par le MEME mapper que
     la grande liste : c'est ce qui donne ici les colonnes assuré, homologue et dates,
     restées vides tant que le serveur n'envoyait que numéro et statut.

     Le repli sur le membre lui-même n'est pas décoratif : les réponses assemblées
     juste après une liaison n'ont pas de dossier chargé, et le numéro comme le
     statut y vivent en propre. */
  const lignes = (membres || []).map((m) => ({ ...(m.sinistre || {}), ...m }));

  const colonnes = [
    {
      title: t('tableMembresLiaison.colonnes.dossier'), key: 'dossier', width: 300,
      render: (_, m) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {onOuvrir ? (
              <button
                onClick={(e) => { e.stopPropagation(); onOuvrir(m.sinistreTrackingId); }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1a6b3c', fontWeight: 800, fontSize: 13 }}
                title={m.numeroSinistreLocal}
              >
                {m.numeroSinistreLocal || '-'}
              </button>
            ) : (
              <strong style={{ color: '#1a6b3c', fontSize: 13 }}>{m.numeroSinistreLocal || '-'}</strong>
            )}
            <Tag color={TYPE_META[m.typeSinistre]?.color || 'default'} style={{ borderRadius: 14, fontSize: 10, fontWeight: 800, marginInlineEnd: 0 }}>
              {TYPE_META[m.typeSinistre]?.label || m.typeSinistre || '-'}
            </Tag>
            <Tag color={DOMMAGE_META[m.typeDommage]?.color || 'default'} style={{ borderRadius: 14, fontSize: 10, marginInlineEnd: 0 }}>
              {DOMMAGE_META[m.typeDommage]?.label || m.typeDommage || '-'}
            </Tag>
            {m.canal && <BadgeCanal canal={m.canal} size="sm" />}
            <PastilleNonAffecte sinistre={m} size="sm" />
            {m.repriseHistorique && <Tag color="purple" style={{ borderRadius: 14, fontSize: 10, fontWeight: 800, marginInlineEnd: 0 }}>R</Tag>}
            {m.moinsAvance && (
              <Tooltip title={t('tableMembresLiaison.moinsAvance')}>
                <WarningFilled style={{ color: '#d97706' }} />
              </Tooltip>
            )}
          </div>
          <MetaLine>{m.numeroSinistreHomologue || m.numeroSinistreAssureur}</MetaLine>
          <MetaLine>{TYPE_META[m.typeSinistre]?.text}</MetaLine>
        </div>
      ),
    },
    {
      title: t('tableMembresLiaison.colonnes.assure'), key: 'assure', width: 210,
      render: (_, m) => (
        <div style={{ minWidth: 0 }}>
          <MetaLine strong>{m.assureNomComplet}</MetaLine>
          <MetaLine>{m.assureImmatriculation ? t('tableMembresLiaison.immat', { valeur: m.assureImmatriculation }) : null}</MetaLine>
          <MetaLine>{(m.assureNumeroPolice || m.numeroPoliceAssureur) ? t('tableMembresLiaison.police', { numero: m.assureNumeroPolice || m.numeroPoliceAssureur }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: t('tableMembresLiaison.colonnes.homologue'), key: 'homologue', width: 210,
      render: (_, m) => (
        <div style={{ minWidth: 0 }}>
          <MetaLine strong>{m.paysEmetteurLibelle || m.paysGestionnaireLibelle}</MetaLine>
          <MetaLine>{m.organismeMembreRaisonSociale || m.organismeHomologueRaisonSociale}</MetaLine>
          <MetaLine>{m.organismeMembreCode ? t('tableMembresLiaison.code', { code: m.organismeMembreCode }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: t('tableMembresLiaison.colonnes.avancement'), dataIndex: 'statut', key: 'statut', width: 190,
      render: (v, m) => {
        const s = STATUT_MAP[v] || { label: v || '-', color: 'default' };
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Tag color={s.color} style={{ borderRadius: 18, fontSize: 11, fontWeight: 650, marginInlineEnd: 0 }}>{s.label}</Tag>
              {m.estContentieux && <Tag color="red" style={{ borderRadius: 18, fontSize: 11, marginInlineEnd: 0 }}>{t('tableMembresLiaison.litige')}</Tag>}
              {m.estPrefinance && <Tag color="gold" style={{ borderRadius: 18, fontSize: 11, marginInlineEnd: 0 }}>{t('tableMembresLiaison.prefinance')}</Tag>}
            </div>
            {renderRc(m)}
          </div>
        );
      },
    },
    {
      title: t('tableMembresLiaison.colonnes.date'), dataIndex: 'dateDeclaration', key: 'date', width: 130,
      render: (v, m) => (
        <div>
          <MetaLine strong>{formatDate(v)}</MetaLine>
          <MetaLine>{m.dateAccident ? t('tableMembresLiaison.accident', { date: formatDate(m.dateAccident) }) : null}</MetaLine>
        </div>
      ),
    },
    {
      title: '', key: 'actions', width: 90,
      render: (_, m) => (
        <Tooltip title={t('tableMembresLiaison.ouvrirDetail')}>
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); if (onOuvrir) onOuvrir(m.sinistreTrackingId); }}>
            {t('tableMembresLiaison.details')}
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ background: '#f9fafb', padding: '8px 12px 8px 40px' }}>
      <Table
        className="cb-table"
        rowKey="sinistreTrackingId"
        dataSource={lignes}
        columns={colonnes}
        pagination={false}
        size="small"
        rowClassName={(m) => `${m.moinsAvance ? 'liaison-membre-moins-avance' : ''}${onOuvrir ? ' cb-ligne-cliquable' : ''}`}
        /* La ligne entière ouvre le dossier, comme dans la grande liste. Réserver
           l'ouverture au numéro et au bouton « Détails » obligeait à viser deux
           cibles de quelques millimètres - et cliquer ailleurs ne faisait rien,
           sans que rien n'indique pourquoi. */
        onRow={(m) => (onOuvrir
          ? { onClick: () => onOuvrir(m.sinistreTrackingId), style: { cursor: 'pointer' } }
          : {})}
      />
    </div>
  );
}